import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { User, Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useAuth } from "@/hooks/useAuth";

export function Navigation() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();
  const { session, profile, signOut, loading } = useAuth();

  const isActive = (path: string) => location.pathname === path;
  // Prefer Google's given_name if available in the Supabase session metadata.
  const givenName = session?.user?.user_metadata?.given_name || session?.user?.user_metadata?.first_name;
  const rawDisplay = givenName ?? profile?.display_name ?? session?.user.email ?? "User";
  const displayName = String(rawDisplay).includes(" ")
    ? String(rawDisplay).split(" ")[0]
    : String(rawDisplay).split("@")[0];

  return (
    <nav className="sticky top-0 z-50 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-b">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Brand */}
          <Link to="/" className="flex items-center gap-2">
            <img 
              src="/lucen-logo.png" 
              alt="Lucen - AP Study Prep" 
              className="h-[140px] w-auto object-contain"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            <Link
              to="/subjects"
              className={cn(
                "text-sm font-medium transition-smooth hover:text-primary",
                isActive("/subjects") ? "text-primary" : "text-muted-foreground"
              )}
            >
              Subjects
            </Link>
            <Link
              to="/dashboard"
              className={cn(
                "text-sm font-medium transition-smooth hover:text-primary",
                isActive("/dashboard") ? "text-primary" : "text-muted-foreground"
              )}
            >
              Dashboard
            </Link>
          </div>

          {/* Auth buttons (search removed per request) */}
          <div className="hidden md:flex items-center space-x-4">
            {session ? (
              <>
                <span className="text-sm text-muted-foreground">Hi, {displayName}</span>
                <Button variant="outline" size="sm" onClick={() => signOut()} disabled={loading}>
                  Sign out
                </Button>
              </>
            ) : (
              <>
                <Button variant="outline" size="sm" asChild>
                  <Link to="/auth?mode=signin">
                    <User className="h-4 w-4" />
                    <span>Sign in</span>
                  </Link>
                </Button>
                <Button size="sm" asChild>
                  <Link to="/auth">Get Started</Link>
                </Button>
              </>
            )}
          </div>

          {/* Mobile menu button */}
          <Button
            variant="ghost"
            size="sm"
            className="md:hidden"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            {isMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </Button>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="md:hidden py-4 border-t">
            <div className="flex flex-col space-y-4">
              <Link
                to="/subjects"
                className={cn(
                  "text-sm font-medium transition-smooth hover:text-primary",
                  isActive("/subjects") ? "text-primary" : "text-muted-foreground"
                )}
                onClick={() => setIsMenuOpen(false)}
              >
                Subjects
              </Link>
              <Link
                to="/dashboard"
                className={cn(
                  "text-sm font-medium transition-smooth hover:text-primary",
                  isActive("/dashboard") ? "text-primary" : "text-muted-foreground"
                )}
                onClick={() => setIsMenuOpen(false)}
              >
                Dashboard
              </Link>
              <div className="pt-4 border-t space-y-2">
                {session ? (
                  <>
                    <div className="text-sm text-muted-foreground">Signed in as {displayName}</div>
                    <Button variant="outline" className="w-full" onClick={() => signOut()} disabled={loading}>
                      Sign out
                    </Button>
                  </>
                ) : (
                  <>
                    <Button variant="outline" className="w-full" asChild>
                      <Link to="/auth?mode=signin">
                        <User className="h-4 w-4 mr-2" />
                        <span>Sign in</span>
                      </Link>
                    </Button>
                    <Button className="w-full" asChild>
                      <Link to="/auth">Get Started</Link>
                    </Button>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}